import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Tag, brl, dataBR, diasAte } from "../components/ui";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/* Comunicados do sistema (avisos da Selo Systems para todos os escritórios).
   Cada um pode ser dispensado; os códigos dispensados ficam no localStorage
   para o aviso não voltar a cada visita. */
const CHAVE_DISPENSADOS = "comunicados_dispensados";

const COMUNICADO_ESTILO = {
    info: { cor: "var(--color-accent)", rotulo: "Comunicado" },
    aviso: { cor: "#b5651d", rotulo: "⚠ Aviso" },
    urgente: { cor: "#a32b1d", rotulo: "⚠ Urgente" }
};

function lerDispensados() {
    try {
        const salvos = JSON.parse(localStorage.getItem(CHAVE_DISPENSADOS));
        return Array.isArray(salvos) ? salvos.map(Number) : [];
    } catch {
        return [];
    }
}

function partes(valor) {
    const [data, hora] = String(valor || "").split(/[ T]/);
    const [a, m, d] = data.split("-").map(Number);
    return { dia: d, mes: MESES[m - 1], ano: a, hora: (hora || "").slice(0, 5) };
}

function saudacao() {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
}

/** Rótulo curto de proximidade em dias CORRIDOS (compromissos, pagamentos) */
function quando(valor) {
    const dias = diasAte(valor);
    if (dias === 0) return "hoje";
    if (dias === 1) return "amanhã";
    if (dias < 0) return `há ${Math.abs(dias)}d`;
    return `em ${dias}d`;
}

/** Rótulo curto em dias ÚTEIS — para prazos processuais (CPC art. 219).
    Recebe o número já calculado no servidor (campo `dias` de /dashboard). */
function quandoUteis(dias) {
    if (dias === 0) return "hoje";
    if (dias === 1) return "1 dia útil";
    if (dias < 0) return `há ${Math.abs(dias)}d úteis`;
    return `em ${dias}d úteis`;
}

const Inicio = () => {
    const navigate = useNavigate();
    const { usuario } = useAuth();
    const [dados, setDados] = useState(null);
    const [assinatura, setAssinatura] = useState(null);
    const [comunicados, setComunicados] = useState([]);
    const [dispensados, setDispensados] = useState(lerDispensados);

    useEffect(() => {
        api.get("/dashboard").then((r) => setDados(r.data)).catch(() => {});
        api.get("/assinatura").then((r) => setAssinatura(r.data.assinatura)).catch(() => {});
        api.get("/comunicados/ativos").then((r) => setComunicados(r.data)).catch(() => {});
    }, []);

    const dispensar = (cod) => {
        setDispensados((atuais) => {
            const novos = atuais.includes(cod) ? atuais : [...atuais, cod];
            try {
                localStorage.setItem(CHAVE_DISPENSADOS, JSON.stringify(novos));
            } catch { /* modo privado / storage cheio: some só nesta sessão */ }
            return novos;
        });
    };

    const comunicadosVisiveis = comunicados.filter((com) => !dispensados.includes(com.cod_comunicado));

    const c = dados?.contadores;

    // Números-chave do topo; cada um leva à sua página
    const METRICAS = [
        { rotulo: "Meus compromissos", valor: c?.meus_compromissos, apoio: "próximos 7 dias", rota: "/agenda" },
        { rotulo: "Prazos pendentes", valor: c?.prazos_pendentes, apoio: "no escritório", rota: "/prazos" },
        { rotulo: "Contratos ativos", valor: c?.contratos_ativos, apoio: "em andamento", rota: "/contratos" },
        { rotulo: "A receber", valor: dados ? brl(dados.financeiro.a_receber) : null, apoio: "saldo dos contratos", rota: "/pagamentos", destaque: true }
    ];

    return (
        <div>
            <Header />

            <main className="page">
                <div>
                    <h2 style={{ margin: 0 }}>{saudacao()}, {usuario?.nome?.split(" ")[0]}.</h2>
                    <p className="text-muted" style={{ margin: "4px 0 0" }}>
                        Aqui está o resumo do que precisa da sua atenção.
                    </p>
                </div>

                {/* Comunicados do sistema — iguais para todos os escritórios */}
                {comunicadosVisiveis.map((com) => {
                    const estilo = COMUNICADO_ESTILO[com.tipo] || COMUNICADO_ESTILO.info;
                    return (
                        <div
                            key={com.cod_comunicado}
                            className="card"
                            style={{
                                flexDirection: "row", alignItems: "flex-start", gap: 12,
                                borderLeft: `4px solid ${estilo.cor}`,
                                background: `color-mix(in srgb, ${estilo.cor} 8%, var(--color-surface))`
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 }}>
                                <span className="card-kicker" style={{ color: estilo.cor }}>{estilo.rotulo}</span>
                                <strong style={{ fontFamily: "var(--font-heading)", fontSize: 17 }}>{com.titulo}</strong>
                                <span className="card-body">{com.mensagem}</span>
                                {com.expira_em && (
                                    <span className="text-muted" style={{ fontSize: 13 }}>
                                        Válido até {dataBR(com.expira_em)}
                                    </span>
                                )}
                            </div>
                            <Button
                                variante="ghost"
                                className="btn-icon"
                                aria-label={`Dispensar aviso: ${com.titulo}`}
                                title="Dispensar"
                                onClick={() => dispensar(com.cod_comunicado)}
                            >
                                ✕
                            </Button>
                        </div>
                    );
                })}

                {/* Aviso de assinatura (vencida ou vencendo) */}
                {assinatura && assinatura.data_vencimento && (
                    assinatura.vencida ? (
                        <button
                            className="card"
                            onClick={() => navigate("/assinatura")}
                            style={{ textAlign: "left", cursor: "pointer", borderLeft: "4px solid #a32b1d", background: "color-mix(in srgb, #a32b1d 8%, var(--color-surface))" }}
                        >
                            <span className="card-kicker" style={{ color: "#a32b1d" }}>⚠ Assinatura vencida</span>
                            <span className="card-body">
                                Sua assinatura venceu em <strong>{dataBR(assinatura.data_vencimento)}</strong>
                                {typeof assinatura.dias_para_vencer === "number" && ` (há ${Math.abs(assinatura.dias_para_vencer)} dia${Math.abs(assinatura.dias_para_vencer) === 1 ? "" : "s"})`}.
                                O acesso pode ser <strong>cancelado a qualquer momento</strong>. Clique para regularizar.
                            </span>
                        </button>
                    ) : typeof assinatura.dias_para_vencer === "number" && (
                        <p className="text-muted" style={{ margin: 0, fontSize: 14 }}>
                            Assinatura ativa · vence em <strong>{assinatura.dias_para_vencer} dia{assinatura.dias_para_vencer === 1 ? "" : "s"}</strong> ({dataBR(assinatura.data_vencimento)}).
                        </p>
                    )
                )}

                <div className="grid-cards">
                    {METRICAS.map((m) => (
                        <button key={m.rotulo} className="card metrica-clicavel" onClick={() => navigate(m.rota)}>
                            <span className="card-kicker">{m.rotulo}</span>
                            <span style={{
                                fontSize: m.destaque ? 26 : 32, fontFamily: "var(--font-heading)", fontWeight: 800,
                                fontVariantNumeric: "tabular-nums", lineHeight: 1.1
                            }}>
                                {m.valor ?? "—"}
                            </span>
                            <span className="card-body text-muted">{m.apoio}</span>
                        </button>
                    ))}
                </div>

                <div className="inicio-colunas">
                    {/* Compromissos da semana */}
                    <Card>
                        <div className="inicio-secao-topo">
                            <h3 style={{ margin: 0 }}>Sua semana</h3>
                            <button className="btn btn-ghost" onClick={() => navigate("/agenda")}>Ver agenda</button>
                        </div>
                        {dados?.compromissos.length ? dados.compromissos.map((comp) => {
                            const p = partes(comp.data_compromisso);
                            return (
                                <button key={comp.cod_compromisso} className="inicio-item" onClick={() => navigate("/agenda")}>
                                    <span className="inicio-item-data">
                                        <strong>{p.dia}</strong>{p.mes}<small>{p.hora}</small>
                                    </span>
                                    <span className="inicio-item-corpo">
                                        <span className="inicio-item-titulo">{comp.nome_compromisso}</span>
                                        <span className="inicio-item-sub">
                                            {[comp.local_compromisso, comp.nome_cliente].filter(Boolean).join(" · ") || "sem local"}
                                        </span>
                                    </span>
                                    <Tag tom={diasAte(comp.data_compromisso) <= 1 ? "danger" : "warn"}>{quando(comp.data_compromisso)}</Tag>
                                </button>
                            );
                        }) : <p className="text-muted" style={{ margin: 0 }}>Nenhum compromisso nos próximos 7 dias.</p>}
                    </Card>

                    {/* Prazos da semana — contagem em DIAS ÚTEIS (CPC art. 219) */}
                    <Card>
                        <div className="inicio-secao-topo">
                            <h3 style={{ margin: 0 }} title="Prazos processuais contados em dias úteis (exclui fins de semana, feriados e recesso forense)">
                                Prazos próximos <span className="text-muted" style={{ fontSize: 12, fontWeight: 400 }}>· dias úteis</span>
                            </h3>
                            <button className="btn btn-ghost" onClick={() => navigate("/prazos")}>Ver prazos</button>
                        </div>
                        {dados?.prazos.length ? dados.prazos.map((pr) => (
                            <button key={pr.cod_prazo} className="inicio-item" onClick={() => navigate("/prazos")}>
                                <span className="inicio-item-corpo">
                                    <span className="inicio-item-titulo">
                                        {pr.nome_prazo}{!!pr.fatal && <> <Tag tom="danger">fatal</Tag></>}
                                    </span>
                                    <span className="inicio-item-sub">{pr.nome_cliente} · {pr.num_processo}</span>
                                </span>
                                <Tag tom={pr.fatal || pr.dias <= 1 ? "danger" : "warn"}>{quandoUteis(pr.dias)}</Tag>
                            </button>
                        )) : <p className="text-muted" style={{ margin: 0 }}>Nenhum prazo nos próximos 7 dias úteis.</p>}
                    </Card>
                </div>

                {/* Financeiro */}
                <Card>
                    <div className="inicio-secao-topo">
                        <h3 style={{ margin: 0 }}>Financeiro</h3>
                        <button className="btn btn-ghost" onClick={() => navigate("/pagamentos")}>Ver pagamentos</button>
                    </div>
                    {dados && (
                        <div className="inicio-financeiro">
                            <div>
                                <span className="card-kicker" style={{ color: "#a32b1d" }}>Vencido</span>
                                <div className="inicio-fin-valor" style={{ color: dados.financeiro.total_vencido > 0 ? "#a32b1d" : "inherit" }}>
                                    {brl(dados.financeiro.total_vencido)}
                                </div>
                                <span className="text-muted" style={{ fontSize: 13.5 }}>
                                    {dados.financeiro.vencidos.length} pagamento(s)
                                </span>
                            </div>
                            <div>
                                <span className="card-kicker">A vencer (7 dias)</span>
                                <div className="inicio-fin-valor">{brl(dados.financeiro.total_a_vencer)}</div>
                                <span className="text-muted" style={{ fontSize: 13.5 }}>
                                    {dados.financeiro.a_vencer.length} pagamento(s)
                                </span>
                            </div>
                            <div>
                                <span className="card-kicker">Total a receber</span>
                                <div className="inicio-fin-valor">{brl(dados.financeiro.a_receber)}</div>
                                <span className="text-muted" style={{ fontSize: 13.5 }}>saldo dos contratos</span>
                            </div>
                        </div>
                    )}

                    {dados?.financeiro.vencidos.length > 0 && (
                        <ul className="mini-lista" style={{ marginTop: 8 }}>
                            {dados.financeiro.vencidos.map((v) => (
                                <li key={v.cod_pag}>
                                    <span style={{ color: "#a32b1d" }}>⚠</span>
                                    {v.nome_cliente} — {brl(v.valorPago)}
                                    <span className="text-muted" style={{ marginLeft: "auto" }}>
                                        venceu {dataBR(v.data_vencimento)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </main>
        </div>
    );
};

export default Inicio;
